<template>
  <div class="grid gap-1">
    <label v-if="label" :for="id" class="text-base">
      <slot v-if="$slots.label" name="label" v-bind="{ label, value: model }"/>
      <template v-else>
        {{ label }}
      </template>
    </label>

    <p v-if="help || $slots.help" :id="aria['aria-describedby']" class="help text-sm text-gray-500">
      <slot v-if="$slots.help" name="help"/>
      <template v-else>
        {{ help }}
      </template>
    </p>

    <div class="flex items-center gap-3">
      <l-button
        class="flex items-center text-lg p-2 -m-2 dark:hover:text-blue-100 disabled:pointer-events-none disabled:text-gray-300"
        :disabled="model <= min"
        aria-label="Decrease"
        @click="decrease"
      >
        <icon name="decrease" set="regular"/>
      </l-button>

      <input
        v-model.number="model"
        class="bg-transparent flex-1 w-full max-w-sm py-3 appearance-none cursor-pointer"
        type="range"
        v-bind="{ id, min, max, name, step, ...aria }"
      >

      <l-button
        class="flex items-center text-lg p-2 -m-2 dark:hover:text-blue-100 disabled:pointer-events-none disabled:text-gray-300"
        :disabled="model >= max"
        aria-label="Increase"
        @click="increase"
      >
        <icon name="increase" set="regular"/>
      </l-button>
    </div>
  </div>
</template>

<script setup>
import Icon from "@/components/Icon.vue"
import LButton from "@/components/buttons/LButton.vue"
import colors from "@/colors"
import { computed, useSlots } from "vue"
import { uniqueId } from "lodash-es"

const model = defineModel([Boolean, Number, String])

const props = defineProps({
  id: { type: String, default: () => `input-${uniqueId()}` },
  name: String,
  label: String,
  help: String,
  step: { type: Number, default: 1 },
  min: { type: Number, required: true },
  max: { type: Number, required: true },
})

const slots = useSlots()

const percent = computed(() => {
  const range = props.max - props.min
  const value = model.value - props.min

  const result = Math.round((value / range) * 100)

  return result || 0
})

const theme = computed(() => ({
  fillPercent: `${percent.value}%`,
  rangeColor: colors.blue[600],
  trackColor: colors.gray[300],
}))

const aria = computed(() => {
  const attrs = {}

  if (props.help || slots.help) {
    attrs["aria-describedby"] = `${props.id}_help`
  }

  return attrs
})

const increase = () => {
  if (model.value < props.max) {
    model.value += props.step
  }
}

const decrease = () => {
  if (model.value > props.min) {
    model.value -= props.step
  }
}
</script>

<style scoped>
  /* stylelint-disable function-no-unknown */

  /******** Chrome, Safari, Opera and Edge Chromium styles ********/

  /* slider track */
  input::-webkit-slider-runnable-track {
    @apply bg-gray-300 rounded-full h-2;

    background: linear-gradient(
      to right,
      v-bind("theme.rangeColor") v-bind("theme.fillPercent"),
      v-bind("theme.trackColor") v-bind("theme.fillPercent") 100%
    );
  }

  /* slider thumb */
  input::-webkit-slider-thumb {
    @apply appearance-none h-7 w-4 rounded-full bg-blue-600 shadow-md -translate-y-2.5;
  }

  input:focus::-webkit-slider-thumb {
    outline: 2px solid theme("colors.blue.500");
    outline-offset: 0.125rem;
  }

  /*********** Firefox styles ***********/

  /* slider track */
  input::-moz-range-track {
    @apply bg-gray-300 rounded-full h-2;

    background: linear-gradient(
      to right,
      v-bind("theme.rangeColor") v-bind("theme.fillPercent"),
      v-bind("theme.trackColor") v-bind("theme.fillPercent") 100%
    );
  }

  /* slider thumb */
  input::-moz-range-thumb {
    @apply h-8 w-4 rounded-full bg-blue-600 shadow-md border-0;
  }

  input:focus::-moz-range-thumb{
    outline: 2px solid theme("colors.blue.500");
    outline-offset: 0.125rem;
  }
</style>
